import React, {useState, useEffect, useCallback} from 'react';
import {View, StyleSheet, SectionList} from 'react-native';
import {Card, Text, ActivityIndicator} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import DateRangeFilter from './components/DateRangeFilter';
import {useReportStore} from '../../store/useReportStore';
import {colors, spacing} from '../../theme';

const formatDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const ReportSalesTab = ({orgUnitId}) => {
  const {t} = useTranslation();
  const {sales, isLoading, fetchSales} = useReportStore();
  const now = new Date();
  const [fromDate, setFromDate] = useState(formatDate(new Date(now.getFullYear(), now.getMonth(), 1)));
  const [toDate, setToDate] = useState(formatDate(now));

  const loadData = useCallback(() => {
    if (fromDate && toDate) {
      fetchSales(fromDate, toDate, orgUnitId);
    }
  }, [fromDate, toDate, orgUnitId, fetchSales]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const sections = (sales?.groups || []).map(group => ({
    title: group.customerName,
    subtotal: group.subtotal,
    data: group.rows,
  }));

  const formatCurrency = (val) => `₹${Number(val).toLocaleString('en-IN')}`;

  const renderSectionHeader = ({section}) => (
    <View style={styles.sectionHeader}>
      <Text style={styles.sectionTitle}>{section.title}</Text>
      <Text style={styles.sectionSubtotal}>{formatCurrency(section.subtotal)}</Text>
    </View>
  );

  const renderItem = ({item}) => (
    <View style={styles.row}>
      <Text style={styles.rowDate}>{item.orderDate}</Text>
      <Text style={styles.rowOrderNum}>{item.orderNumber}</Text>
      <Text style={styles.rowAmount}>{formatCurrency(item.totalAmount)}</Text>
    </View>
  );

  return (
    <View style={styles.container}>
      <DateRangeFilter
        fromDate={fromDate}
        toDate={toDate}
        onFromChange={setFromDate}
        onToChange={setToDate}
      />

      {isLoading ? (
        <ActivityIndicator style={styles.loader} size="large" color={colors.primary} />
      ) : sections.length > 0 ? (
        <>
          <Card style={styles.grandTotalCard} mode="elevated">
            <Card.Content style={styles.grandTotalContent}>
              <Text style={styles.grandTotalLabel}>{t('report.grandTotal')}</Text>
              <Text style={styles.grandTotalValue}>{formatCurrency(sales?.grandTotal || 0)}</Text>
            </Card.Content>
          </Card>
          <SectionList
            sections={sections}
            keyExtractor={(item, index) => `${item.orderNumber}-${index}`}
            renderSectionHeader={renderSectionHeader}
            renderItem={renderItem}
            contentContainerStyle={styles.listContent}
          />
        </>
      ) : (
        <Text style={styles.noData}>{t('report.noReportData')}</Text>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  loader: {marginTop: spacing.xl},
  grandTotalCard: {
    marginHorizontal: spacing.md,
    marginBottom: spacing.sm,
    backgroundColor: colors.primary,
  },
  grandTotalContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  grandTotalLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.white,
  },
  grandTotalValue: {
    fontSize: 20,
    fontWeight: 'bold',
    color: colors.white,
  },
  listContent: {paddingHorizontal: spacing.md, paddingBottom: spacing.xl},
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: colors.primaryLight + '20',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    marginTop: spacing.sm,
    borderRadius: 6,
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.primary,
  },
  sectionSubtotal: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.text,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.divider,
    backgroundColor: colors.surface,
  },
  rowDate: {
    width: 90,
    fontSize: 13,
    color: colors.textSecondary,
  },
  rowOrderNum: {
    flex: 1,
    fontSize: 13,
    color: colors.text,
  },
  rowAmount: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.text,
  },
  noData: {
    textAlign: 'center',
    color: colors.textSecondary,
    marginTop: spacing.xl,
  },
});

export default ReportSalesTab;
